/**
 * Pill — kleine farbige Marker (z.B. „Bald" auf disabled Sidebar-Items,
 * Status-Marker in Listen, Filter-Chips). Pure präsentational, kein State.
 *
 * Eingeführt in Slice-1 Redesign (2026-05-08).
 *
 * Tones (Palette-reduziert auf grün/gelb/sand/rot — 2026-06-09):
 *   neutral — helles Grün-Grau (ink-100)
 *   green   — brand-green-100/700
 *   warn    — brand-yellow (Warn/Info)
 *   live    — red (live-status)
 *   sand    — brand-sand (für „Bald"-Markierung disabled Items)
 *   expiring — orange (läuft bald ab, `--color-expiring-*`, Redesign S2)
 *
 * `blue` wurde entfernt (Audit Item 3): hatte keine Caller und war ohnehin
 * auf grün aliased — es gibt kein Blau in der Palette.
 */
import type { ReactNode } from 'react';

export type PillTone = 'neutral' | 'green' | 'warn' | 'live' | 'sand' | 'expiring';

type Props = {
  children: ReactNode;
  tone?: PillTone;
  className?: string;
  /**
   * Optionaler accessible name für den Render-`<span>`. Nutzen wenn das
   * Pill-Children nur eine Zahl/Glyph ist und der Screen-Reader-Kontext
   * ohne Label sonst leer wäre (z.B. Sidebar-Counter "3 Elemente im
   * Papierkorb" statt nacktem "3"). Nicht setzen wenn die children-Text
   * selbst bereits self-explanatory ist — sonst Doppel-Reading.
   */
  'aria-label'?: string;
};

const TONE_CLASSES: Record<PillTone, string> = {
  neutral: 'bg-ink-100 text-brand-green-800',
  green: 'bg-brand-green-100 text-brand-green-700',
  warn: 'bg-brand-yellow text-brand-green-700',
  live: 'bg-red-50 text-red-700',
  sand: 'bg-brand-sand text-brand-green-800',
  expiring: 'bg-expiring-100 text-expiring-800',
};

export function Pill({
  children,
  tone = 'neutral',
  className = '',
  'aria-label': ariaLabel,
}: Props) {
  return (
    <span
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1 px-2 py-[1px] rounded-full text-xs2 font-medium whitespace-nowrap ${TONE_CLASSES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}
